import {
  IDayReportResponse,
  ILossParetoResponse,
  IMachineDayResponse,
  IShiftReviewResponse,
  ITimelineResponse,
  ITimelineSegmentResponse,
  ITrendResponse,
  IWorkerDayResponse,
} from 'types/api/operationsAimApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { LossBucket } from 'types/operationsEnums.interface'

export const DEMO_MACHINE_DAYS: IMachineDayResponse[] = [
  { machineId: 1, name: 'Truck 03', utilizationPct: 86, availabilityPct: 94, loads: 41, tonnes: 1845, breakdowns: 0, costOfDay: 12400 },
  { machineId: 2, name: 'Truck 05', utilizationPct: 78, availabilityPct: 91, loads: 37, tonnes: 1665, breakdowns: 0, costOfDay: 11900 },
  { machineId: 3, name: 'Truck 08', utilizationPct: 71, availabilityPct: 88, loads: 33, tonnes: 1485, breakdowns: 1, costOfDay: 13650 },
  { machineId: 4, name: 'Truck 12', utilizationPct: 42, availabilityPct: 55, loads: 18, tonnes: 810, breakdowns: 0, costOfDay: 9800 },
  { machineId: 5, name: 'CAT320', utilizationPct: 82, availabilityPct: 96, loads: 52, tonnes: 2340, breakdowns: 0, costOfDay: 15200 },
  { machineId: 6, name: 'Dozer D1', utilizationPct: 31, availabilityPct: 44, loads: 0, tonnes: 0, breakdowns: 2, costOfDay: 7300 },
]

export const DEMO_WORKER_DAYS: IWorkerDayResponse[] = [
  { workerId: 1, name: 'Peter M.', tasksDone: 12, activeHours: 10.8, breaksMin: 75, issuesReported: 1 },
  { workerId: 2, name: 'Michael K.', tasksDone: 11, activeHours: 10.2, breaksMin: 50, issuesReported: 0 },
  { workerId: 3, name: 'John S.', tasksDone: 10, activeHours: 10.5, breaksMin: 45, issuesReported: 2 },
  { workerId: 4, name: 'Daniel R.', tasksDone: 6, activeHours: 8.1, breaksMin: 55, issuesReported: 0 },
  { workerId: 5, name: 'Anna V.', tasksDone: 9, activeHours: 10.5, breaksMin: 50, issuesReported: 1 },
  { workerId: 6, name: 'Nicholas T.', tasksDone: 8, activeHours: 11.3, breaksMin: 45, issuesReported: 3 },
  { workerId: 7, name: 'Thomas P.', tasksDone: 14, activeHours: 10.1, breaksMin: 55, issuesReported: 0 },
  { workerId: 8, name: 'Robert L.', tasksDone: 7, activeHours: 8.9, breaksMin: 55, issuesReported: 1 },
  { workerId: 9, name: 'Grace M.', tasksDone: 9, activeHours: 10.2, breaksMin: 50, issuesReported: 2 },
]

const P = LossBucket.Productive
const B = LossBucket.Breakdown
const PM = LossBucket.PlannedMaint
const NO = LossBucket.NoOperator
const NT = LossBucket.NoTask
const BX = LossBucket.BlockedCrossDept
const WB = LossBucket.WeatherBlastHold

const MACHINE_PATTERNS: { [k: number]: [LossBucket, number, string?][] } = {
  1: [[P, 150], [NT, 20], [P, 170], [NT, 45], [P, 160], [BX, 30], [P, 145]],
  2: [[NO, 40], [P, 180], [NT, 25], [P, 150], [WB, 60], [P, 130], [P, 135]],
  3: [[P, 120], [B, 75], [PM, 45], [P, 160], [NT, 30], [P, 150], [P, 140]],
  4: [[PM, 240], [NT, 60], [P, 120], [NT, 90], [P, 130], [NT, 80]],
  5: [[P, 200], [NT, 15], [P, 180], [BX, 40], [P, 170], [P, 115]],
  6: [[B, 210], [PM, 120], [NT, 90], [P, 90], [B, 130], [NT, 80]],
}

const WORKER_PATTERNS: { [k: number]: [LossBucket, number, string?][] } = {
  1: [[P, 150], [NT, 15, 'Rest pause'], [P, 155], [NT, 40, 'Meal break'], [P, 160], [NT, 20, 'Rest pause'], [P, 180]],
  2: [[P, 210], [NT, 35, 'Meal break'], [P, 150], [WB, 60], [NT, 15, 'Rest pause'], [P, 250]],
  3: [[P, 120], [NT, 45], [NT, 30, 'Meal break'], [P, 205], [NT, 15, 'Rest pause'], [P, 305]],
  4: [[NT, 90], [P, 120], [NT, 40, 'Meal break'], [NT, 90], [P, 130], [NT, 15, 'Rest pause'], [P, 235]],
  5: [[P, 200], [NT, 15, 'Rest pause'], [P, 150], [NT, 35, 'Meal break'], [BX, 40], [P, 280]],
  6: [[P, 160], [NT, 30, 'Meal break'], [P, 200], [NT, 15, 'Rest pause'], [P, 315]],
  7: [[P, 160], [NT, 40, 'Meal break'], [P, 150], [NT, 60], [P, 170], [NT, 15, 'Rest pause'], [P, 125]],
  8: [[NT, 70], [P, 140], [NT, 40, 'Meal break'], [P, 160], [NT, 60], [NT, 15, 'Rest pause'], [P, 235]],
  9: [[P, 190], [NT, 15, 'Rest pause'], [P, 170], [NT, 35, 'Meal break'], [NT, 60], [P, 250]],
}

const dayFromParams = (params: any): string => {
  const raw = params?.from || params?.date
  if (typeof raw === 'string' && raw.length >= 10) return raw.slice(0, 10)
  return new Date().toISOString().slice(0, 10)
}

const segmentsFromPattern = (
  pattern: [LossBucket, number, string?][],
  day: string,
): ITimelineSegmentResponse[] => {
  const [y, m, d] = day.split('-').map(Number)
  let cursor = new Date(y, m - 1, d, 6, 0, 0)
  return pattern.map(([bucket, minutes, note]) => {
    const from = cursor
    cursor = new Date(from.getTime() + minutes * 60000)
    return { bucket, from: from.toISOString(), to: cursor.toISOString(), note: note ?? null }
  })
}

export const demoMachineTimeline = (id: number, params: any): ITimelineResponse => ({
  entityId: id,
  segments: segmentsFromPattern(
    MACHINE_PATTERNS[((id - 1) % 6) + 1] || MACHINE_PATTERNS[1],
    dayFromParams(params),
  ),
  provisional: false,
})

export const demoWorkerTimeline = (id: number, params: any): ITimelineResponse => ({
  entityId: id,
  segments: segmentsFromPattern(
    WORKER_PATTERNS[((id - 1) % 9) + 1] || WORKER_PATTERNS[1],
    dayFromParams(params),
  ),
  provisional: false,
})

export const demoShiftReview = (params: any): IShiftReviewResponse => ({
  shiftDate: dayFromParams(params),
  targetTonnes: 9000,
  actualTonnes: 8145,
  fiveShiftAvgTonnes: 8420,
  waterfall: {
    target: 9000,
    actual: 8145,
    buckets: [
      { bucket: B, hours: 6.9, costValue: 20900 },
      { bucket: PM, hours: 7.7, costValue: 15400 },
      { bucket: NT, hours: 9.2, costValue: 13100 },
      { bucket: NO, hours: 1.7, costValue: 5100 },
      { bucket: BX, hours: 1.2, costValue: 3600 },
      { bucket: WB, hours: 1.0, costValue: 3000 },
    ],
    unattributed: 0.6,
  },
  exceptions: [
    'Dozer D1 down 5.7h — hydraulic hose failure, parts fitted 14:20',
    'Truck 12 planned service overran by 1.5h',
    'Blast hold 10:00–11:00 cleared without incident',
  ],
  provisional: false,
})

export const DEMO_LOSSES: ILossParetoResponse = {
  groupBy: 'machine',
  items: [
    { key: 'Dozer D1', hours: 9.4, costValue: 28300 },
    { key: 'Truck 12', hours: 7.1, costValue: 14100 },
    { key: 'Truck 08', hours: 3.1, costValue: 9200 },
    { key: 'Truck 05', hours: 2.4, costValue: 6300 },
    { key: 'CAT320', hours: 1.2, costValue: 4400 },
    { key: 'Truck 03', hours: 1.0, costValue: 2900 },
  ],
}

const TREND_BASE: { [metric: string]: { base: number; swing: number } } = {
  tonnes: { base: 8300, swing: 900 },
  utilization: { base: 74, swing: 9 },
  availability: { base: 86, swing: 7 },
  cost: { base: 68000, swing: 9000 },
  loads: { base: 180, swing: 26 },
}

export const demoTrend = (metric: string): ITrendResponse => {
  const cfg = TREND_BASE[metric] || TREND_BASE.tonnes
  const points = Array.from({ length: 14 }, (_, i) => {
    const d = new Date()
    d.setDate(d.getDate() - (13 - i))
    const wave = Math.sin(i * 1.15) * cfg.swing + (i % 5 === 3 ? -cfg.swing * 0.6 : 0)
    return { date: d.toISOString().slice(0, 10), value: Math.round(cfg.base + wave) }
  })
  return { metric, points }
}

export const demoDayReports = (): IPaginatedResponse<IDayReportResponse> => {
  const data = Array.from({ length: 12 }, (_, i) => {
    const d = new Date()
    d.setDate(d.getDate() - i)
    return {
      reportDate: d.toISOString().slice(0, 10),
      locked: i > 1,
      tonnes: 7600 + Math.round(Math.sin(i * 1.4) * 850) + (i % 4 === 2 ? -600 : 200),
      tasksCompleted: 34 + ((i * 7) % 11),
    }
  })
  return { data, totalCount: data.length, totalPages: 1 }
}
